/**
 * SupervisorSpecialtyQueue — panel shown on the Staff Dashboard for
 * users with role='supervisor'. Lists the specialty service requests
 * that Ops has routed to this supervisor (either by name or by region
 * scope). From here the supervisor can further assign a specific crew
 * member or team to actually execute the job.
 *
 * The Ops manager's Schedule Crew form (AdminSpecialtyServices)
 * assigns a Regional Supervisor via radio pick. That request lands
 * here, and the supervisor picks the actual agents.
 */
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Sparkles, MapPin, Calendar, Users, CheckCircle2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

// Human-readable label for internal service keys — matches the
// AdminSpecialtyServices catalog. Extracted here so this panel stays
// independent from that (larger) admin component.
const SERVICE_LABELS = {
  pressure_washing: 'Pressure Washing',
  dumpster_treatment: 'Dumpster Treatment',
  window_cleaning: 'Window Cleaning',
  bulk_removal: 'Bulk Removal',
  eco_friendly_amenities: 'Eco-Friendly Amenities',
  trash_room_maintenance: 'Trash Room Maintenance', // legacy
};

export default function SupervisorSpecialtyQueue({ user }) {
  const [requests, setRequests] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assign, setAssign] = useState({ open: false, request: null, agents: [] });

  const load = async () => {
    setLoading(true);
    try {
      const [rReq, rEmp] = await Promise.all([
        fetch(`${API}/api/service-requests`, { credentials: 'include' }),
        fetch(`${API}/api/company/employees`, { credentials: 'include' }),
      ]);
      if (rReq.ok) setRequests(await rReq.json());
      if (rEmp.ok) {
        const data = await rEmp.json();
        setTeam(Array.isArray(data) ? data : (data.employees || []));
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Only surface specialty (non-bulk-pickup and non-standard) requests
  // this supervisor should act on. The backend endpoint already scopes
  // by supervisor name / region; here we further filter to specialty
  // types + open statuses so the panel stays focused.
  const OPEN_STATES = ['pending', 'submitted', 'scheduled', 'in_progress'];
  const mine = useMemo(
    () => requests.filter((r) => SERVICE_LABELS[r.request_type] && OPEN_STATES.includes(r.status)),
    [requests],
  );

  // Crew candidates for delegation — the supervisor's own team by
  // region + role. Exclude other supervisors + blocked users.
  const crewCandidates = useMemo(() => {
    const region = user?.region_code || null;
    return (team || []).filter((e) => {
      if (e.is_blocked) return false;
      if (e.user_id === user?.user_id) return false;
      const role = (e.role || '').toLowerCase();
      if (!['agent', 'crew_leader', 'driver', 'valet', 'staff', 'site_agent'].includes(role)) return false;
      if (region && e.region_code && e.region_code !== region) return false;
      return true;
    });
  }, [team, user]);

  const confirmAssign = async () => {
    if (!assign.request || assign.agents.length === 0) {
      return toast.error('Pick at least one crew member');
    }
    const agentNames = assign.agents.map((uid) => {
      const emp = team.find((e) => e.user_id === uid);
      return emp ? (emp.name || emp.badge_number || uid) : uid;
    });
    const res = await fetch(
      `${API}/api/service-requests/${assign.request.request_id}?status=in_progress`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigned_agents: agentNames }),
      },
    );
    if (res.ok) {
      toast.success(`Crew assigned to ${SERVICE_LABELS[assign.request.request_type] || 'job'}`);
      setAssign({ open: false, request: null, agents: [] });
      load();
    } else {
      toast.error('Failed to assign');
    }
  };

  if (loading) return null;
  if (mine.length === 0) return null;

  return (
    <>
      <Card className="bg-zinc-900 border-zinc-700 p-4 mb-4" data-testid="supervisor-specialty-queue">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-white flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#C9A962]" /> My Specialty Assignments
          </h3>
          <Badge className="bg-[#C9A962] text-black font-bold">{mine.length}</Badge>
        </div>

        <ul className="space-y-2">
          {mine.map((r) => (
            <li key={r.request_id}
                className="border border-zinc-800 rounded-md p-3 flex items-start justify-between gap-3 hover:border-[#C9A962]/40"
                data-testid={`supervisor-specialty-row-${r.request_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="bg-purple-800 text-white text-[10px]">
                    {SERVICE_LABELS[r.request_type] || r.request_type}
                  </Badge>
                  <Badge variant="outline" className="border-zinc-600 text-zinc-300 text-[10px]">
                    {r.status?.replace('_', ' ') || 'open'}
                  </Badge>
                  {r.priority === 'urgent' && (
                    <Badge className="bg-amber-500 text-black text-[10px]">URGENT</Badge>
                  )}
                </div>
                <p className="text-white font-semibold text-sm">{r.property_name || '—'}</p>
                <p className="text-xs text-zinc-400 flex items-center gap-1 mt-0.5">
                  <MapPin className="h-3 w-3" />
                  {r.region_code || '—'}
                  {r.scheduled_date && (
                    <>
                      <span className="mx-1">·</span>
                      <Calendar className="h-3 w-3" />
                      {new Date(r.scheduled_date).toLocaleDateString()}
                    </>
                  )}
                </p>
                <p className="text-xs text-zinc-500 line-clamp-2 mt-1">{r.description}</p>
                {r.assigned_agents && r.assigned_agents.length > 1 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {r.assigned_agents
                      .filter((a) => a !== user?.name && a !== user?.user_id)
                      .map((name, i) => (
                        <Badge key={i} variant="outline"
                               className="border-emerald-600 text-emerald-300 text-[10px] font-normal">
                          <Users className="h-2.5 w-2.5 mr-1" />{name}
                        </Badge>
                      ))}
                  </div>
                )}
              </div>
              <Button
                size="sm"
                onClick={() => setAssign({ open: true, request: r, agents: [] })}
                className="bg-[#C9A962] text-black font-bold hover:bg-[#B89A56]"
                data-testid={`supervisor-specialty-assign-${r.request_id}`}
              >
                {r.assigned_agents && r.assigned_agents.length > 1 ? 'Update Crew' : 'Assign Crew'}
              </Button>
            </li>
          ))}
        </ul>
      </Card>

      <Dialog open={assign.open}
              onOpenChange={(v) => !v && setAssign({ open: false, request: null, agents: [] })}>
        <DialogContent
          className="bg-white text-[#2D2D2D] border border-[#C9A962]/40 sm:max-w-md max-h-[80vh] overflow-y-auto p-4"
        >
          <DialogHeader>
            <DialogTitle className="text-[#2D2D2D]">Assign Crew</DialogTitle>
          </DialogHeader>
          {assign.request && (
            <div className="space-y-3 pt-1">
              <div className="bg-[#FAF7EF] border border-[#C9A962]/30 rounded-md p-2.5 text-sm">
                <div className="font-bold">{SERVICE_LABELS[assign.request.request_type]}</div>
                <div className="text-xs text-zinc-600 mt-0.5">
                  {assign.request.property_name} · {assign.request.region_code || '—'}
                </div>
                <p className="text-xs text-zinc-700 mt-1 line-clamp-3">{assign.request.description}</p>
              </div>

              <div>
                <Label className="text-[#2D2D2D] font-semibold text-sm flex items-center gap-1">
                  <Users className="h-4 w-4" /> My team ({assign.agents.length} selected)
                </Label>
                <div className="border border-zinc-300 rounded-md bg-white divide-y divide-zinc-200 max-h-40 overflow-y-auto mt-1">
                  {crewCandidates.length === 0 ? (
                    <div className="p-3 text-xs text-zinc-500 italic">
                      No crew members in your region yet — request more via Operations.
                    </div>
                  ) : crewCandidates.map((e) => {
                    const uid = e.user_id;
                    const checked = assign.agents.includes(uid);
                    return (
                      <label key={uid}
                             className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-zinc-50"
                             data-testid={`supervisor-specialty-crew-${uid}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(ev) => setAssign((s) => {
                            const set = new Set(s.agents);
                            if (ev.target.checked) set.add(uid); else set.delete(uid);
                            return { ...s, agents: [...set] };
                          })}
                        />
                        <span className="font-semibold text-[#2D2D2D]">{e.name || uid}</span>
                        <span className="text-zinc-500 font-mono">{e.badge_number || ''}</span>
                        <span className="ml-auto text-zinc-500 capitalize">{e.role || 'agent'}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline"
                    onClick={() => setAssign({ open: false, request: null, agents: [] })}>
              Cancel
            </Button>
            <Button onClick={confirmAssign}
                    className="bg-[#C9A962] text-black font-bold hover:bg-[#B89A56]"
                    data-testid="supervisor-specialty-confirm">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
